/** One simulation step in milliseconds (66.67 Hz). Every per-tick constant below assumes it. */
export const TICK_MS = 15;

/** The backdrop picture's size in pixels: spots and taps use these coordinates. */
export const BACKDROP_WIDTH = 1200;
export const BACKDROP_HEIGHT = 958;

/** Round lengths offered in the menu, in seconds. */
export const ROUND_SECONDS = [30, 60, 90] as const;
export type RoundSeconds = (typeof ROUND_SECONDS)[number];
export const DEFAULT_ROUND_SECONDS: RoundSeconds = 60;

/** "3, 2, 1, Kör!" before the first light (3 s). */
export const COUNTDOWN_TICKS = 200;

/** How fast a round is, chosen in the menu (I-004): Lugn, Normal, Snabb. */
export const DIFFICULTIES = ["calm", "normal", "fast"] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];
export const DEFAULT_DIFFICULTY: Difficulty = "normal";

/** Normal's pace. Lights on at once: rises from the first to the second number over the round. */
export const LIT_AT_START = 2;
export const LIT_AT_END = 4;
/** How long a light stays on: 3 s at the start of the round, 2 s at the end. */
export const LIGHT_LIFE_START = 200;
export const LIGHT_LIFE_END = 133;
/** Ticks between two lights switching on (random in this range). */
export const LIGHT_GAP_MIN = 15;
export const LIGHT_GAP_MAX = 55;
/** A spot stays dark this long after its light goes out or is collected. */
export const SPOT_REST_TICKS = 80;

/** Colours a light can have; the view maps the index to its palette. */
export const LIGHT_COLOR_COUNT = 6;
/** One light in this many is golden. */
export const GOLDEN_ONE_IN = 12;
export const POINTS = 1;
export const GOLDEN_POINTS = 3;

/** Points per second of round for two and three stars at Normal (one star is always given). */
export const TWO_STARS_PER_SECOND = 0.3;
export const THREE_STARS_PER_SECOND = 0.55;

/** A difficulty's pace (ticks, as above) and how it scales the stars' score rates. */
export interface Pace {
	litStart: number;
	litEnd: number;
	lifeStart: number;
	lifeEnd: number;
	gapMin: number;
	gapMax: number;
	starScale: number;
}

export const PACE: Readonly<Record<Difficulty, Readonly<Pace>>> = {
	// Fewer lights that stay on longer (4.5 s → 3 s) and come less often.
	calm: {
		litStart: 1,
		litEnd: 3,
		lifeStart: 300,
		lifeEnd: 200,
		gapMin: 30,
		gapMax: 80,
		starScale: 0.7,
	},
	normal: {
		litStart: LIT_AT_START,
		litEnd: LIT_AT_END,
		lifeStart: LIGHT_LIFE_START,
		lifeEnd: LIGHT_LIFE_END,
		gapMin: LIGHT_GAP_MIN,
		gapMax: LIGHT_GAP_MAX,
		starScale: 1,
	},
	// More lights that go out sooner (2.25 s → 1.5 s) and come more often.
	fast: {
		litStart: 3,
		litEnd: 5,
		lifeStart: 150,
		lifeEnd: 100,
		gapMin: 10,
		gapMax: 40,
		starScale: 1.3,
	},
};

/**
 * Minuspoäng (I-007, off by default): a tap that hits no light costs this many points, so
 * tapping wildly doesn't pay. The score never goes below 0.
 */
export const MISS_PENALTY = 1;

/**
 * Hit streaks (Svit, off by default): every STREAK_EVERY collects in a row give a bonus that
 * grows the longer the streak lasts: +1 at 5, +2 at 10, +3 at 15… What breaks a streak:
 * - mild: a tap in the dark or on a trick light;
 * - strict: also a light that goes out uncollected (trick lights excepted).
 */
export const STREAK_MODES = ["off", "mild", "strict"] as const;
export type StreakMode = (typeof STREAK_MODES)[number];
export const DEFAULT_STREAK_MODE: StreakMode = "off";
export const STREAK_EVERY = 5;

/** The bonus for reaching `streak` collects in a row (0 unless it is a multiple of STREAK_EVERY). */
export function streakBonus(streak: number): number {
	return streak > 0 && streak % STREAK_EVERY === 0 ? streak / STREAK_EVERY : 0;
}

/**
 * Trick lights (I-002), only with Minuspoäng: one light in this many is an Oogie Boogie light
 * (green and spiky) that costs TRICK_PENALTY points when tapped, never below 0.
 */
export const TRICK_ONE_IN = 7;
export const TRICK_PENALTY = 2;

/**
 * Mardröm (nightmare mode): no time limit, the pace keeps rising, and the round ends when the
 * lives are gone. A tap in the dark, a tapped trick light and a light going out uncollected each
 * cost a life. Always with trick lights and Sträng streaks, so all runs compare fairly.
 */
export const NIGHTMARE_LIVES = 10;
/** After this long (45 s) the pace is half-way from its start to its limit, and so on. */
export const NIGHTMARE_RAMP_TICKS = 3000;
export const NIGHTMARE_LIFE_START = 200;
export const NIGHTMARE_LIFE_MIN = 50;
export const NIGHTMARE_GAP_START = 45;
export const NIGHTMARE_GAP_MIN = 6;
export const NIGHTMARE_LIT_START = 2;
export const NIGHTMARE_LIT_MAX = 8;
/** One more light at once every this long (20 s). */
export const NIGHTMARE_LIT_EVERY_TICKS = 1333;

/** Which bonus visitors a round has (Bonus in the menu): bats, ghosts, both or neither. */
export const EXTRAS = ["both", "bats", "ghosts", "none"] as const;
export type Extras = (typeof EXTRAS)[number];
export const DEFAULT_EXTRAS: Extras = "both";

/** Bats (P2.2): now and then one flutters across the sky; catching it gives BAT_POINTS. */
export const BAT_POINTS = 5;
/** Ticks between one bat leaving and the next coming (9 to 15 s), and the first after "Kör!". */
export const BAT_GAP_MIN = 600;
export const BAT_GAP_MAX = 1000;
/** A bat crosses the picture in this many ticks (4.5 s). */
export const BAT_FLIGHT_TICKS = 300;
/** Its reach in picture pixels (a tap's own reach comes on top), it is a big target. */
export const BAT_RADIUS = 70;
/** It flies in the sky band above the set, in picture pixels, bobbing up and down. */
export const BAT_SKY_TOP = 90;
export const BAT_SKY_BOTTOM = 300;
export const BAT_BOB = 45;
/** Up-and-down swoops during one crossing. */
export const BAT_SWOOPS = 3;

/** Ghosts: now and then one slides out from a hiding place (`hides.ts`) and back. */
export const GHOST_POINTS = 5;
/** The first one comes this long after "Kör!" (6 s), so short rounds get to see it. */
export const GHOST_FIRST_TICKS = 400;
/** Ticks between one ghost hiding and the next coming (10.5 to 16.5 s). */
export const GHOST_GAP_MIN = 700;
export const GHOST_GAP_MAX = 1100;
/** Sliding out and back takes 0.45 s each; it stays out 1.2 s in between. */
export const GHOST_SLIDE_TICKS = 30;
export const GHOST_OUT_TICKS = 80;
/** It can be caught from when this much of it (0 to 1) is out, not while it is hidden. */
export const GHOST_CATCH_PEEK = 0.25;
